import { regularApiClient } from "../utils/kiota-client";
import type {
  CoolingRegimeProductDto,
  UpsertCommand2,
} from "../../../RoyalFloraHolland.Aap.Services.Api.Web.Kiota/Typescript/Api/models/index";

/**
 * Interface for updating cooling regime product temperatures
 */
export interface UpdateCoolingRegimeProductTemperaturesPayload {
  coolingRegimeProductId: number;
  shortTermMainCoolingTemperatureId?: number | null;
  shortTermFallbackCoolingTemperatureId?: number | null;
  longTermMainCoolingTemperatureId?: number | null;
  longTermFallbackCoolingTemperatureId?: number | null;
  shortTermDisplacementMainCoolingTemperatureId?: number | null;
  shortTermDisplacementFallbackCoolingTemperatureId?: number | null;
  longTermDisplacementMainCoolingTemperatureId?: number | null;
  longTermDisplacementFallbackCoolingTemperatureId?: number | null;
}

/**
 * Interface for updating cooling regime product priorities
 */
export interface UpdateCoolingRegimeProductPrioritiesPayload {
  coolingRegimeProductId: number;
  shortTermCoolingPriorityId?: number | null;
  longTermCoolingPriorityId?: number | null;
  /** Optional relative humidity indication update ('J' | 'N' | null) */
  relativeHumidityIndication?: string | null;
}

/**
 * Update cooling temperatures for a cooling regime product
 */
export async function updateCoolingRegimeProductTemperatures(
  payload: UpdateCoolingRegimeProductTemperaturesPayload,
): Promise<CoolingRegimeProductDto> {
  try {
    const { coolingRegimeProductId, ...updateData } = payload;

    // First, get the current data
    const currentData = await regularApiClient.api
      .vWithVersion("1")
      .coolingRegimeProducts.byId(coolingRegimeProductId)
      .get();

    if (!currentData) {
      throw new Error("Cooling regime product not found");
    }

    const productCode = (currentData as any).productCode;
    if (productCode === undefined || productCode === null) {
      throw new Error("Missing productCode on cooling regime product");
    }

    // Merge current data with updates
    const updatedData = {
      ...currentData,
      coolingTemperatureCodeShortTerm:
        updateData.shortTermMainCoolingTemperatureId ??
        currentData.coolingTemperatureCodeShortTerm,
      coolingTemperatureCodeLongTerm:
        updateData.longTermMainCoolingTemperatureId ??
        currentData.coolingTemperatureCodeLongTerm,
      coolingTemperatureCodeShortTermDisplacement:
        updateData.shortTermDisplacementMainCoolingTemperatureId ??
        currentData.coolingTemperatureCodeShortTermDisplacement,
      coolingTemperatureCodeLongTermDisplacement:
        updateData.longTermDisplacementMainCoolingTemperatureId ??
        currentData.coolingTemperatureCodeLongTermDisplacement,
    };

    // Send PUT request
    await regularApiClient.api
      .vWithVersion("1")
      .coolingRegimeProducts.byId(coolingRegimeProductId)
      .put(updatedData);

    // Fetch and return the updated data by product code
    const response = await regularApiClient.api
      .vWithVersion("1")
      .coolingRegimeProducts.byCode.byCode(productCode)
      .get();

    if (!response) {
      throw new Error("Failed to fetch updated cooling regime product");
    }

    return response as CoolingRegimeProductDto;
  } catch (error) {
    throw new Error(
      error instanceof Error
        ? error.message
        : "Failed to update cooling regime product temperatures",
    );
  }
}

/**
 * Update cooling priorities for a cooling regime product
 */
export async function updateCoolingRegimeProductPriorities(
  payload: UpdateCoolingRegimeProductPrioritiesPayload,
): Promise<CoolingRegimeProductDto> {
  try {
    const { coolingRegimeProductId, ...updateData } = payload;

    // First, get the current data
    const currentData = await regularApiClient.api
      .vWithVersion("1")
      .coolingRegimeProducts.byId(coolingRegimeProductId)
      .get();

    if (!currentData) {
      throw new Error("Cooling regime product not found");
    }

    const productCode = (currentData as any).productCode;
    if (productCode === undefined || productCode === null) {
      throw new Error("Missing productCode on cooling regime product");
    }

    // Merge current data with updates
    const updatedData = {
      ...currentData,
      coolingPriorityCodeShortTerm:
        updateData.shortTermCoolingPriorityId ??
        currentData.coolingPriorityCodeShortTerm,
      coolingPriorityCodeLongTerm:
        updateData.longTermCoolingPriorityId ??
        currentData.coolingPriorityCodeLongTerm,
      relativeHumidityIndication:
        updateData.relativeHumidityIndication ??
        currentData.relativeHumidityIndication,
    };

    // Send PUT request
    await regularApiClient.api
      .vWithVersion("1")
      .coolingRegimeProducts.byId(coolingRegimeProductId)
      .put(updatedData);
    // Fetch and return the updated data by product code
    const response = await regularApiClient.api
      .vWithVersion("1")
      .coolingRegimeProducts.byCode.byCode(productCode)
      .get();

    if (!response) {
      throw new Error("Failed to fetch updated cooling regime product");
    }

    return response as CoolingRegimeProductDto;
  } catch (error) {
    throw new Error(
      error instanceof Error
        ? error.message
        : "Failed to update cooling regime product priorities",
    );
  }
}

/**
 * Delete a cooling regime product by ID
 */
export async function deleteCoolingRegimeProduct(
  coolingRegimeProductId: number,
): Promise<void> {
  try {
    await regularApiClient.api
      .vWithVersion("1")
      .coolingRegimeProducts.byId(coolingRegimeProductId)
      .delete();
  } catch (error) {
    throw new Error(
      error instanceof Error
        ? error.message
        : "Failed to delete cooling regime product",
    );
  }
}

/**
 * Create or upsert a cooling regime product
 */
export async function upsertCoolingRegimeProduct(
  body: UpsertCommand2,
): Promise<CoolingRegimeProductDto> {
  try {
    const response = await regularApiClient.api
      .vWithVersion("1")
      .coolingRegimeProducts.post(body);

    if (!response) {
      throw new Error("Failed to create cooling regime product");
    }

    return response as CoolingRegimeProductDto;
  } catch (error) {
    throw new Error(
      error instanceof Error
        ? error.message
        : "Failed to create cooling regime product",
    );
  }
}
